import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeRaw from 'rehype-raw';
import 'highlight.js/styles/github-dark.css';

interface MarkdownRendererProps {
    content: string;
    className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
    return (
        <div className={`prose prose-lg max-w-none ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                    rehypeRaw,
                    rehypeHighlight,
                    rehypeSlug,
                    [rehypeAutolinkHeadings, { behavior: 'wrap' }],
                ]}
                components={{
                    // Custom styling for different elements
                    h2: ({ ...props }) => (
                        <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" {...props} />
                    ),
                    h3: ({ ...props }) => (
                        <h3 className="text-xl font-bold text-gray-800 mt-8 mb-3" {...props} />
                    ),
                    p: ({ ...props }) => (
                        <p className="text-gray-700 mb-4 leading-relaxed" {...props} />
                    ),
                    a: ({ ...props }) => (
                        <a
                            className="text-primary hover:text-primary-light underline transition-colors"
                            {...props}
                        />
                    ),
                    ul: ({ ...props }) => (
                        <ul className="list-disc pl-6 space-y-2 mb-6 text-gray-700" {...props} />
                    ),
                    ol: ({ ...props }) => (
                        <ol className="list-decimal pl-6 space-y-2 mb-6 text-gray-700" {...props} />
                    ),
                    blockquote: ({ ...props }) => (
                        <blockquote
                            className="border-l-4 border-primary pl-4 py-2 my-6 bg-blue-50 rounded-r-lg italic text-gray-700"
                            {...props}
                        />
                    ),
                    code: (props) => {
                        const { className, children, ...rest } = props;
                        const isInline = !className || !className.includes('language-');

                        if (isInline) {
                            return (
                                <code
                                    className="bg-gray-100 text-primary px-1.5 py-0.5 rounded text-sm font-mono"
                                    {...rest}
                                >
                                    {children}
                                </code>
                            );
                        }
                        return <code className={className} {...rest}>{children}</code>;
                    },
                    pre: ({ ...props }) => (
                        <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto my-6" {...props} />
                    ),
                    img: ({ ...props }) => (
                        <img className="rounded-lg my-6 w-full" {...props} alt={(props.alt as string) || ''} />
                    ),
                    table: ({ ...props }) => (
                        <div className="overflow-x-auto my-6 -mx-4 px-4 sm:mx-0 sm:px-0">
                            <table className="min-w-full border-collapse text-sm sm:text-base" {...props} />
                        </div>
                    ),
                    thead: ({ ...props }) => (
                        <thead className="bg-gray-50" {...props} />
                    ),
                    th: ({ ...props }) => (
                        <th
                            className="border border-gray-200 px-3 py-2 text-left font-semibold text-gray-900 whitespace-normal"
                            {...props}
                        />
                    ),
                    td: ({ ...props }) => (
                        <td
                            className="border border-gray-200 px-3 py-2 align-top text-gray-700 whitespace-normal"
                            {...props}
                        />
                    ),
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
};
